import { Box, Chip, type ChipProps } from "@mui/material";
import { tagColors } from "@zfp-akademie-a-s/zfp-design";
import { BlockedIcon, CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { InProgressIcon } from "Components/_Shared/Icons/Icons";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

type Props = {
  readonly state: AnalysisDetailState;
  readonly variant?: "filled" | "outlined";
  readonly size?: "small" | "medium";
  readonly disabled?: boolean;
  // drawn after the label in the leading icon's colour and spacing (e.g. a select's chevron)
  readonly endIcon?: React.ReactElement;
};

// the chip colour per state; its text and icon colours are tagColors[colour], the chip tokens
export const analysisStatusColor: Record<AnalysisDetailState, ChipProps["color"] & keyof typeof tagColors> = {
  [AnalysisDetailState.in_progress]: "info",
  [AnalysisDetailState.completed]: "success",
  [AnalysisDetailState.declined]: "warning",
};

export const AnalysisStatusChip: React.FC<Props> = ({ state, variant = "outlined", size = "medium", disabled, endIcon }) => {
  const { t } = useResource();

  const getText = (state: AnalysisDetailState): string => {
    switch (state) {
      case AnalysisDetailState.in_progress:
        return t(Resources.AnalysesListing.Status.InProgress);
      case AnalysisDetailState.completed:
        return t(Resources.AnalysesListing.Status.Completed);
      case AnalysisDetailState.declined:
        return t(Resources.AnalysesListing.Status.Declined);
    }
  };

  const getIcon = (state: AnalysisDetailState): React.ReactElement => {
    switch (state) {
      case AnalysisDetailState.in_progress:
        return <InProgressIcon />;
      case AnalysisDetailState.completed:
        return <CheckCircleIcon />;
      case AnalysisDetailState.declined:
        return <BlockedIcon />;
    }
  };

  const label = endIcon ? (
    <Box component="span" display="inline-flex" alignItems="center" gap={2}>
      {getText(state)}
      <Box component="span" display="inline-flex" color={tagColors[analysisStatusColor[state]].icon}>
        {endIcon}
      </Box>
    </Box>
  ) : (
    getText(state)
  );

  return <Chip size={size} variant={variant} color={analysisStatusColor[state]} icon={getIcon(state)} label={label} disabled={disabled} />;
};
